import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Landmark, Trophy, Shield, GraduationCap, Award } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { EventList } from "../components/EventList";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CountryDetailPage() {
  const { countryId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    window.scrollTo(0, 0);
    axios.get(`${API}/countries/${countryId}`).then((r) => {
      setData(r.data);
      axios.post(`${API}/track`, { type: "Country", name: r.data.country.name, link: `/countries/${countryId}` }).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [countryId]);

  if (notFound) return <div className="py-24 text-center text-slate-500">Country not found. <Link to="/countries" className="text-[#0055FF] underline">All countries</Link></div>;
  if (!data) return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0055FF] border-t-transparent" /></div>;

  const { country: c, teams, leagues, schools, hall_of_fame, events } = data;
  const facts = [
    ["Continent", c.continent], ["Capital", c.capital || "—"],
    ["Olympic committee", c.olympic_committee || "—"],
    ["States / provinces", c.states.length],
  ];

  return (
    <div>
      <PageHero topic={`${c.name} sport stadium fans`} title={`${c.flag} ${c.name}`} subtitle={`The sporting life of ${c.name} — national passions, local clubs and the places where communities play.`} listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <Link to="/countries" data-testid="back-to-countries" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0055FF] hover:underline"><ArrowLeft className="h-4 w-4" /> <T>All Countries & Places</T></Link>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-1" data-testid="country-facts">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{c.flag}</span>
              <h2 className="text-lg font-bold text-slate-900"><T>Country Facts</T></h2>
            </div>
            <dl className="mt-4 space-y-3">
              {facts.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2 text-sm">
                  <dt className="font-bold text-slate-500"><T>{k}</T></dt>
                  <dd className="text-right text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
            {(c.top_sports || []).length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400"><T>Top sports</T></p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.top_sports.map((s) => <span key={s} className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-[#0055FF]">{s}</span>)}
                </div>
              </div>
            )}
            {c.federations && <p className="mt-4 flex items-start gap-1.5 text-xs text-slate-600"><Trophy className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /><span><span className="font-bold"><T>Federations</T>:</span> {c.federations.join(", ")}</span></p>}
            {c.halls_of_fame && <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-600"><Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /><span><span className="font-bold"><T>Halls of fame</T>:</span> {c.halls_of_fame.join(", ")}</span></p>}
          </div>
          <div className="lg:col-span-2">
            <AIContent topic={`Sporting culture of ${c.name}`} context={`Write a short sporting-culture overview of ${c.name} (${c.continent}). Cover popular sports, participation traditions and how communities play. Speak in general, well-known terms — do not invent specific clubs, statistics, results or federation names.`} />
          </div>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="country-places">
          <h2 className="text-xl font-bold text-slate-900"><T>States, Provinces & Cities</T></h2>
          {c.states.length === 0 && <p className="mt-3 text-sm text-slate-500"><T>No administrative divisions listed for this country.</T></p>}
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {c.states.map((st) => (
              <div key={st.name} className="rounded-xl bg-slate-50 p-3">
                <p className="flex items-center gap-1.5 text-sm font-bold text-slate-800"><MapPin className="h-3.5 w-3.5 text-[#0055FF]" /> {st.name}</p>
                {st.cities.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {st.cities.map((city) => <Link key={city} to={`/cities/${encodeURIComponent(city)}`} data-testid={`city-link-${city.toLowerCase().replace(/\s/g, "-")}`} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-[#0055FF] hover:border-[#0055FF]">{city} →</Link>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {(teams.length > 0 || leagues.length > 0 || schools.length > 0) && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {teams.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="country-teams">
                <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Shield className="h-5 w-5 text-purple-600" /> <T>Teams</T></h2>
                <div className="mt-3 space-y-2">
                  {teams.map((t) => (
                    <Link key={t.id} to={`/teams/${t.id}`} data-testid={`country-team-${t.id}`} className="block rounded-xl bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-blue-50 hover:text-[#0055FF]">{t.name} <span className="text-xs font-normal text-slate-400">· {t.sport}</span></Link>
                  ))}
                </div>
              </section>
            )}
            {leagues.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="country-leagues">
                <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Trophy className="h-5 w-5 text-amber-500" /> <T>Leagues</T></h2>
                <div className="mt-3 space-y-2">
                  {leagues.map((l) => (
                    <Link key={l.id} to={`/leagues/${l.id}`} data-testid={`country-league-${l.id}`} className="block rounded-xl bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-blue-50 hover:text-[#0055FF]">{l.name} <span className="text-xs font-normal text-slate-400">· {l.sport}</span></Link>
                  ))}
                </div>
              </section>
            )}
            {schools.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="country-schools">
                <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><GraduationCap className="h-5 w-5 text-emerald-600" /> <T>Schools & Colleges</T></h2>
                <div className="mt-3 space-y-2">
                  {schools.map((s) => (
                    <Link key={s.id} to={`/schools/${s.id}`} data-testid={`country-school-${s.id}`} className="block rounded-xl bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-blue-50 hover:text-[#0055FF]">{s.name} <span className="text-xs font-normal text-slate-400">· {s.city}</span></Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {hall_of_fame.length > 0 && (
          <section className="rounded-2xl border border-orange-100 bg-orange-50 p-6" data-testid="country-hof">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Award className="h-5 w-5 text-orange-500" /> <T>Hall of Fame Legends</T></h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {hall_of_fame.map((h) => (
                <div key={h.id} className="rounded-xl bg-white p-4">
                  <p className="text-sm font-bold text-slate-900">{h.name} <span className="text-xs font-normal text-slate-400">· {h.sport}</span></p>
                  <p className="mt-1 text-xs text-slate-500"><T>{h.citation}</T></p>
                </div>
              ))}
            </div>
            <Link to="/halloffame" className="mt-4 inline-block text-xs font-bold text-[#0055FF] hover:underline"><T>Visit the Hall of Fame</T> →</Link>
          </section>
        )}

        {events.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="country-events">
            <h2 className="mb-4 text-xl font-bold text-slate-900"><T>Related Events</T></h2>
            <EventList events={events} />
            <p className="mt-3 text-[11px] italic text-slate-400"><T>Curated demo data — clearly labeled, not a live feed.</T></p>
          </div>
        )}
      </div>
    </div>
  );
}
